import { InjectionToken, NgZone, Provider } from "@angular/core";
import { Store } from "@ngrx/store";
import { PlatformService } from "@obg/ngx.global";
import { LoggingFactory } from "@obg/ngx.global.evo";

import { NoopSignalrService } from "./noop-signalr.service";
import { SignalrConfigService } from "./signalr.config";
import { SignalrService } from "./signalr.service";

const signalrServiceFactory =
	(token: InjectionToken<SignalrService>) =>
	(
		logging: LoggingFactory,
		platformService: PlatformService,
		ngZone: NgZone,
		config: SignalrConfigService,
		coreStore: Store<any>
	): SignalrService | NoopSignalrService => {
		if (platformService.isServer) {
			return new NoopSignalrService(logging, ngZone, config, coreStore, token.toString());
		}

		return new SignalrService(logging, ngZone, config, coreStore, token.toString());
	};

export const SIGNALR_CUSTOMER_TOKEN = new InjectionToken<SignalrService>("SIGNALR_CUSTOMER");
export const SIGNALR_GROUP_TOKEN = new InjectionToken<SignalrService>("SIGNALR_GROUP");

/**
 * Creates a provider for SignalR based on a unique token
 * This can be used to start isolated connection to a RealTime broker and open multiple
 * connection at one time.
 *
 * @example
 * ```
 * // In consumer Library
 * const AREA_SPECIFIC_RT_SERVICE_TOKEN = new InjectionToken("AREA_SPECIFIC_RT_SERVICE");
 *
 * // In consumer NgModule
 * @NgModule({
 *     providers: [
 *         createSignalrProvider(AREA_SPECIFIC_RT_SERVICE_TOKEN)
 *     ]
 * })
 *
 * // In consumer class
 * constructor(
 *     @Inject(AREA_SPECIFIC_RT_SERVICE_TOKEN) areaSpecificRealTimeService: SignalrService
 * )
 * ```
 *
 * @param uniqueToken The distinguishing token that the provider can be located with
 */
export function createSignalrProvider(uniqueToken: InjectionToken<SignalrService>): Provider[] {
	return [
		{
			provide: uniqueToken,
			useFactory: signalrServiceFactory(uniqueToken),
			deps: [LoggingFactory, PlatformService, NgZone, SignalrConfigService, Store<any>],
		},
	];
}
